import IntlMessageFormat from "intl-messageformat";
import { creditRequestType, trainingIndicatorRequestType, portfolioRequestType, gpaRequestType, NCBTRequestType, SETSRequestType, creditPs8ResponseType, creditResponseType, trainingIndicatorResponseType ,portfolioResponseType, gpaResponseType, NCBTResponseType, SETSResponseType } from "../common/type";
import { getCredit, getTrainingIndicator, getPortfolio, getGpaScore, getNCBTScore, getSETSScore} from "../common/http-requests";
import { splitFormatPeriodSemester , formatPeriodGradeSemester, formatSeTsPeriodGradeGraduation } from "../common/util";

// NOTE: 計算誤差回避のためフロントエンド側でも backend と同じものを定義する
const CREDIT_TOTAL_ROUND_VALUE = 2;
const GPA_ROUND_VALUE = 1;
const NCBT_ROUND_VALUE = 1;

// ホーム画面以外のコメント表示画面数
const COMMENT_NUMBER_SCREEN = 6;

// SE・TS画面で'earned_period'、'earned_semester_code'、'graduation_status'のデータ分を減らすための定数
const SE_TS_INDEX_SKIP = 3;

// 概要説明文
export const descriptionText = (nickname: string, language: string, t: any, t_prefix: string) => {
  return new IntlMessageFormat(t(t_prefix + ".description"), language).format({"nickname": nickname});
};

// ホーム画面のコメント生成関数
export const commentHome = async (userId: number, nickname: string, token: any, t: any): Promise<string> => {
  // 乱数を発生させる（0～ホーム画面以外のコメント表示画面数 - 1）
  const randomNumber = _randomInt(COMMENT_NUMBER_SCREEN  - 1);

  // コメント内容を作成
  let commentContent = "";
  try {
    // 単位画面向け
    if (randomNumber === 0) {
      const requestParams: creditRequestType = {
        login_user_id: userId
      };
      const backendResult0: creditResponseType = await getCredit(token, requestParams);
      commentContent = commentCredit(userId, nickname, t, backendResult0);
      commentContent += " [PERSPECTIVE]";
    
    // 育成指標画面向け
    } else if (randomNumber === 1) {
      const requestParams_mext: trainingIndicatorRequestType = {
        login_user_id: userId,   // userResponseType.id の値
        target_type: "mext"      // mext:文科省, tokushima:徳島県
      };
      const backendResult1_mext: trainingIndicatorResponseType = await getTrainingIndicator(token, requestParams_mext);

      const requestParams_tokushima: trainingIndicatorRequestType = {
        login_user_id: userId,   // userResponseType.id の値
        target_type: "tokushima"      // mext:文科省, tokushima:徳島県
      };
      const backendResult1_tokushima: trainingIndicatorResponseType = await getTrainingIndicator(token, requestParams_tokushima);
      commentContent = commentTrainingIndicator(userId, nickname, t, backendResult1_mext, backendResult1_tokushima);
      commentContent += " [INDICATOR]";

    // GPA画面向け
    } else if (randomNumber === 2) {
      const requestParams: gpaRequestType = {
        login_user_id: userId
      };
      const backendResult2: gpaResponseType = await getGpaScore(token, requestParams);
      commentContent = commentGPA(userId, nickname, t, backendResult2);
      commentContent += " [GPA]";
    
    // portfolio画面向け  
    } else if (randomNumber === 3) {
      const requestParams: portfolioRequestType = {
        login_user_id: userId
      };
      const backendResult3: portfolioResponseType = await getPortfolio(token, requestParams);
      commentContent = commentPortfolio(userId, nickname, t, backendResult3);
      commentContent += " [PORTFOLIO]";

    // N-CBT画面向け
    } else if (randomNumber === 4) {
      const requestParams: NCBTRequestType = {
        login_user_id: userId
      };
      const backendResult4: NCBTResponseType = await getNCBTScore(token, requestParams);
      commentContent = commentNCBT(userId, nickname, t, backendResult4);
      commentContent += " [N-CBT]";

    // SE・TS画面向け
    } else {
      const requestParams: SETSRequestType = {
        login_user_id: userId
      };
      const backendResult5: SETSResponseType = await getSETSScore(token, requestParams);
      commentContent = commentSETS(userId, nickname, t, backendResult5);
      commentContent += " [SE・TS]";
    }
  } catch (e: any) {
    return _helloText(nickname, t);
  }
  return commentContent;
};

// 単位画面のコメント生成関数
export const commentCredit = (userId: number, nickname: string, t: any, backendResult: creditResponseType): string => {
  const periodCount = backendResult.period_count;
  if(periodCount === 0) {
    return _helloText(nickname, t);
  }

  const previousCredits: creditPs8ResponseType = backendResult.previous_credits;
  const latestCredits: creditPs8ResponseType = backendResult.latest_credits;

  const labelList: string[] = Array.from({ length: 8 }, (_, i) => t(`common.ps8_${i + 1}`));
  const creditLabelLength = labelList.length;

  // 乱数を発生させる（0～(credit_labelsの長さ*2-1) + 1）
  const randomNumber = _randomInt(creditLabelLength * 2 - 1) + 1;
  const keyIndex = randomNumber % creditLabelLength || creditLabelLength;
  const key = `ps8_credit_${keyIndex}` as keyof creditPs8ResponseType;

  // コメント内容を作成
  try {
      // 2分の1の確率で任意の鳴門パースペクティブの単位を表示、または最新データがあり、前回データがない場合(最新データのみの場合)も含む
      if (randomNumber >= creditLabelLength || periodCount <= 1) {
          return _i18nText(t, "comment.credit_1", {"nickname": nickname, "perspective": labelList[keyIndex - 1], "credit": latestCredits[key]});
      // 2分の1の確率で任意の鳴門パースペクティブの単位の増加幅を表示
      } else {
          const increase = Number(latestCredits[key] - previousCredits[key]).toFixed(CREDIT_TOTAL_ROUND_VALUE);
          return _i18nText(t, "comment.credit_2", {"nickname": nickname, "perspective": labelList[keyIndex - 1], "credit": increase});
        }
  } catch (e) {
      return _helloText(nickname, t);
  }
};

// 育成指標画面のコメント生成関数
export const commentTrainingIndicator = (userId: number, nickname: string, t: any, backendResultMext: trainingIndicatorResponseType, backendResultTokushima: trainingIndicatorResponseType): string => {
  const mextTrainingIndicators: number[] = backendResultMext.training_indicators || [];
  const tokushimaTrainingIndicators: number[] = backendResultTokushima.training_indicators || [];
  const allZeroIndicator = mextTrainingIndicators.every(num => num === 0);
  if (allZeroIndicator === true) {
    return _helloText(nickname, t);
  }
  const allTrainingIndicators: number[] = [
    ...mextTrainingIndicators,
    ...tokushimaTrainingIndicators,
  ];

  const mextLabelList: string[] = Array.from({ length: 17 }, (_, i) => "[" + t("comment.indicator.title_mext") + "] " + t(`indicator.mext_${i + 1}`));
  const tokushimaLabelList: string[] = Array.from({ length: 20 }, (_, i) => "[" + t("comment.indicator.title_tokushima") + "] " + t(`indicator.tokushima_${i + 1}`));
  const labelList = [...mextLabelList, ...tokushimaLabelList];
  const labelListLength = labelList.length;

  // 乱数を発生させる（0～表示項目の数 - 1）
  const randomNumber = _randomInt(labelListLength - 1);

  // コメント内容を作成
  try {
    const selectedLabel = labelList[randomNumber];
    const selectedIndicator = allTrainingIndicators[randomNumber];
    return _i18nText(t, "comment.indicator", {"nickname": nickname, "indicator": selectedLabel, "credit": selectedIndicator});
  } catch (e) {
    return _helloText(nickname, t);
  }
};

// GPA画面のコメント生成関数
export const commentGPA = (userId: number, nickname: string, t: any, backendResult: any): string => {
  const scoreList = backendResult?.scoreList;

  if (!scoreList || scoreList.length === 0) {
    return _helloText(nickname, t);
  }

  // 最新の期間データを取得 
  const latestPeriod = scoreList[scoreList.length - 1];
  const latestSelfScore = latestPeriod["myself"];
  const latestLabel = formatPeriodGradeSemester(latestPeriod["earned_period"], latestPeriod["earned_grade"], latestPeriod["earned_semester_code"], t);

  // 乱数を発生させる（0～スコアリストの長さ）
  const randomNumber = _randomInt(scoreList.length - 1);

  // コメント内容を作成
  try {
    // 最新の値を過去の値と比較表示 例：〇〇さん、2023(3年次)後期は2018(1年次)前期よりも1.5ポイント多い（少ない）ですね
    if (randomNumber < scoreList.length - 1) {
      // 過去の期間データをランダムに取得
      const randomPeriod = scoreList[randomNumber];
      const randomSelfScore = randomPeriod["myself"];
      const randomLabel = formatPeriodGradeSemester(randomPeriod["earned_period"], randomPeriod["earned_grade"], randomPeriod["earned_semester_code"], t);

      const diffPoint = Number((latestSelfScore - randomSelfScore).toFixed(GPA_ROUND_VALUE));
      if (diffPoint > 0) {
        return _i18nText(t, "comment.gpa_1", {"nickname": nickname, "label1": latestLabel, "label2": randomLabel, "point": diffPoint});
      } else if (diffPoint < 0) {
        return _i18nText(t, "comment.gpa_2", {"nickname": nickname, "label1": latestLabel, "label2": randomLabel, "point": Math.abs(diffPoint)});
      } else {
        return _i18nText(t, "comment.gpa_3", {"nickname": nickname, "label1": latestLabel, "label2": randomLabel});
      }

    // 最新の値を単純表示 例：〇〇さん、あなたの2023(3年次)後期のGPAは4.5ですね
    } else {
      return _i18nText(t, "comment.gpa_4", {"nickname": nickname, "label1": latestLabel, "point": latestSelfScore});

    // 仕様落ち：全学生平均
    // 最新の値を全学生平均と比較表示 例：〇〇さん、あなたの2023(3年次)後期のGPAは全学生平均よりも1.5ポイント多い（少ない）ですね
    // } else {
    //   const averageScore = latestPeriod["all_students"];
    //   const diffPoint = Number((latestSelfScore - averageScore).toFixed(GPA_ROUND_VALUE));

    //   if (diffPoint > 0) {
    //     commentContent = `${latestLabel} が全学生平均よりも ${diffPoint} ポイント多いですね`;
    //   } else if (diffPoint < 0) {
    //     commentContent = `${latestLabel} が全学生平均よりも ${Math.abs(diffPoint)} ポイント少ないですね`;
    //   } else {
    //     commentContent = `${latestLabel} が全学生平均と同じですね`;
    //   }
    }
  } catch (e) {
    return _helloText(nickname, t);
  }
};

// portfolio画面のコメント生成関数
export const commentPortfolio = (userId: number, nickname: string, t: any, backendResult: any): string => {
  const dataList: Array<Record<string, any>> = backendResult.portfolioReportDataList;

  // リストの先頭データから期間の数を算出
  const periodCount = Object.keys(dataList[0]).length - 1;

  if (periodCount === 0) {
    return _helloText(nickname, t);
  }

  // パースペクティブの数を算出
  const perspectiveCount = dataList.length;

  // 乱数を発生させる（0～data_list内の全期間データの数-1）
  const randomNumber = _randomInt(perspectiveCount * periodCount - 1);

  // 商（リストにあるポートフォリオ（辞書）データを選択する際に利用）
  const selectedPeriodIndex = Math.floor(randomNumber / perspectiveCount);

  // 余り（ポートフォリオデータにある期間データを選択する際に利用）
  const selectedPortfolioIndex = randomNumber % perspectiveCount;

  // コメント内容を作成
  try {
    const selectedPortfolio = dataList[selectedPortfolioIndex];
    const keysSelectedPortfolio = Object.keys(selectedPortfolio);
    const valuesSelectedPortfolio = Object.values(selectedPortfolio);

    // パースペクティブのラベルを取得
    const selectedPortfolioText = t("portfolio." + selectedPortfolio["categoryLabel"]);

    // 選択されたパースペクティブの値（※+1は、試験日'categoryLabel'のデータ分を減らすため）
    const selectedPeriodText = splitFormatPeriodSemester(keysSelectedPortfolio[selectedPeriodIndex + 1], t);
    const selectedPeriodValue = valuesSelectedPortfolio[selectedPeriodIndex + 1];

    // 最新の期間の文字列
    const latestPeriodText = splitFormatPeriodSemester(keysSelectedPortfolio[periodCount], t);

    // コメント内容の作成
    // 最後のリストの場合は、最新の値を単純表示 例：〇〇さん、構えの2031 後期は1件ですね
    if (selectedPeriodIndex === periodCount - 1) {
      return _i18nText(t, "comment.portfolio_1", {"nickname": nickname, "perspective": selectedPortfolioText, "period1": selectedPeriodText, "point": selectedPeriodValue});
    // 最後のリストではない場合は、最新の値を過去の値と比較表示 例：〇〇さん、構えの2031 後期は2024 前期よりも1件多いですね
    } else {
      const diffNumber = valuesSelectedPortfolio[periodCount] - selectedPeriodValue;
      if (diffNumber > 0) {
        return _i18nText(t, "comment.portfolio_2", {"nickname": nickname, "perspective": selectedPortfolioText, "period1": latestPeriodText, "period2": selectedPeriodText, "point": diffNumber});
      } else if (diffNumber < 0) {
        return _i18nText(t, "comment.portfolio_3", {"nickname": nickname, "perspective": selectedPortfolioText, "period1": latestPeriodText, "period2": selectedPeriodText, "point": Math.abs(diffNumber)});
      } else {
        return _i18nText(t, "comment.portfolio_4", {"nickname": nickname, "perspective": selectedPortfolioText, "period1": latestPeriodText, "period2": selectedPeriodText});
      }
    }
  } catch (e) {
    return _helloText(nickname, t);
  }
};

// N-CBT画面のコメント生成関数
export const commentNCBT = (userId: number, nickname: string, t: any, backendResult: any): string => {
  const scoreList = backendResult.scores;

  if (!scoreList || scoreList.length === 0) {
    return _helloText(nickname, t);
  }

  // リストの先頭データ（構えのデータ）からパースペクティブの数を算出
  const perspectiveCount = Object.keys(scoreList[0]).length - 1;

  // 試験の回数 
  const examCount = scoreList.length;

  // 乱数を発生させる（0～data_list内の全診査結果ポイント-1）
  const randomNumber = _randomInt(examCount * perspectiveCount - 1);

  // 商（リストにあるパースペクティブ（辞書）データを選択する際に利用）
  const selectedExamIndex = Math.floor(randomNumber / perspectiveCount);

  // 余り（パースペクティブにある期間データを選択する際に利用）
  const selectedPerspectiveIndex = randomNumber % perspectiveCount;

  const labelList: string[] = Array.from({ length: 8 }, (_, i) => t(`ncbt.ps_ncbt_${i + 1}`));

  // コメント内容を作成
  try {
    const selectedExam = scoreList[selectedExamIndex];
    const valuesSelectedExam = Object.values(selectedExam);

    const selectedPerspectiveText = labelList[selectedPerspectiveIndex];
    const selectedExamDateValue = selectedExam.exam_date;
    // 選択されたパースペクティブの値（※+1は、試験日'exam_date'のデータ分を減らすため）
    const selectedPerspectiveValue = valuesSelectedExam[selectedPerspectiveIndex + 1];

    const latestExam = scoreList[examCount - 1];
    const latestExamDateValue = latestExam.exam_date;
    const latestPerspectiveValue = Object.values(latestExam)[selectedPerspectiveIndex + 1];

    // 最後のリストの場合は、最新の値を単純表示 例：〇〇さん、2024年05月29日に実施したN-CBTの構えは0.4点ですね
    if (selectedExamIndex === examCount - 1) {
      return _i18nText(t, "comment.ncbt_1", {"nickname": nickname, "perspective": selectedPerspectiveText, "examDate1": latestExamDateValue, "point": latestPerspectiveValue});
    // 最後のリストではない場合は、最新の値を過去の値と比較表示 例：〇〇さん、2024/05/29に実施したN-CBTは2023/5/10よりも0.4点少ないですね
    } else {
      const diffNumber = Number((Number(latestPerspectiveValue) - Number(selectedPerspectiveValue)).toFixed(NCBT_ROUND_VALUE));
      if (diffNumber > 0) {
        return _i18nText(t, "comment.ncbt_2", {"nickname": nickname, "perspective": selectedPerspectiveText, "examDate1": latestExamDateValue, "examDate2": selectedExamDateValue, "point": diffNumber});
      } else if (diffNumber < 0) {
        return _i18nText(t, "comment.ncbt_2", {"nickname": nickname, "perspective": selectedPerspectiveText, "examDate1": latestExamDateValue, "examDate2": selectedExamDateValue, "point": Math.abs(diffNumber)});
      } else {
        return _i18nText(t, "comment.ncbt_2", {"nickname": nickname, "perspective": selectedPerspectiveText, "examDate1": latestExamDateValue, "examDate2": selectedExamDateValue, "point": diffNumber});
      }
    }
  } catch (e) {
    return _helloText(nickname, t);
  }
};

// SE・TS画面のコメント生成関数
export const commentSETS = (userId: number, nickname: string, t: any, backendResult: any): string => {
  const scoreList = backendResult.scores;

  if (!scoreList || scoreList.length === 0) {
    return _helloText(nickname, t);
  }

  // リストの先頭データ（構えのデータ）からパースペクティブの数を算出
  // ※SE_TS_INDEX_SKIP分を削減するのは、'earned_period'、'earned_semester_code'、'graduation_status'のデータ分を減らすため
  const perspectiveCount = Object.keys(scoreList[0]).length - SE_TS_INDEX_SKIP;
  const periodCount = scoreList.length;

  // 乱数を発生させる（0～data_list内の全受検回-1）
  const randomNumber = _randomInt(periodCount * perspectiveCount - 1);

  // 商（リストにあるテスト（辞書）データを選択する際に利用）
  const selectedSurveyIndex = Math.floor(randomNumber / perspectiveCount);
  // 余り（パースペクティブにある期間データを選択する際に利用）
  const selectedPerspectiveIndex = randomNumber % perspectiveCount;

  const seLabelList: string[] = [t("sets.se_survey_1")];
  const tsLabelList: string[] = Array.from({ length: 13 }, (_, i) => t(`sets.ps13_sets_${i + 1}`));
  const labelList = [...seLabelList, ...tsLabelList];

  // コメント内容を作成
  try {
    const selectedSurvey = scoreList[selectedSurveyIndex];
    const valuesSelectedSurvey = Object.values(selectedSurvey);

    const selectedPerspectiveText = labelList[selectedPerspectiveIndex];
    const selectedSurveyPointValue = valuesSelectedSurvey[selectedPerspectiveIndex + SE_TS_INDEX_SKIP];
    const selectedSurveyDateText = _generatePeriodString(selectedSurvey, t);

    const latestSurvey = scoreList[periodCount - 1];
    const valuesLatestSurvey = Object.values(latestSurvey);
    const latestSurveyPointValue = valuesLatestSurvey[selectedPerspectiveIndex + SE_TS_INDEX_SKIP];
    const latestSurveyDateText = _generatePeriodString(latestSurvey, t);

    // 最後のリストの場合は、最新の値を単純表示 例：〇〇さん、2024年下期に実施したSEサーベイの教職自己肯定感は32ポイントですね
    if (selectedSurveyIndex === periodCount - 1) {
      return _i18nText(t, "comment.sets_1", {"nickname": nickname, "perspective": selectedPerspectiveText, "surveyDate1": latestSurveyDateText, "point": latestSurveyPointValue});
    // 最後のリストではない場合は、最新の値を過去の値と比較表示 例：〇〇さん、2023年上期に実施したSEサーベイの教職自己肯定感は2024年下期よりも32ポイント多いですね
    } else {
      const diffPoint = Number(latestSurveyPointValue) - Number(selectedSurveyPointValue);
      if (diffPoint > 0) {
        return _i18nText(t, "comment.ncbt_2", {"nickname": nickname, "perspective": selectedPerspectiveText, "examDate1": latestSurveyDateText, "examDate2": selectedSurveyDateText, "point": diffPoint});
      } else if (diffPoint < 0) {
        return _i18nText(t, "comment.ncbt_2", {"nickname": nickname, "perspective": selectedPerspectiveText, "examDate1": latestSurveyDateText, "examDate2": selectedSurveyDateText, "point": Math.abs(diffPoint)});
      } else {
        return _i18nText(t, "comment.ncbt_2", {"nickname": nickname, "perspective": selectedPerspectiveText, "examDate1": latestSurveyDateText, "examDate2": selectedSurveyDateText});
      }
    }
  } catch (e) {
    return _helloText(nickname, t);
  }
};

// 0オリジンの乱数を発生させる関数
const _randomInt = (maxNumber: number) => {
  return Math.floor(Math.random() * (maxNumber + 1));
};

// 学年を示す文字列を生成する関数
const _generatePeriodString = (surveyData: any, t: any): string => {
  return formatSeTsPeriodGradeGraduation(surveyData.earned_period, surveyData.earned_grade, surveyData.graduation_status, t);
};

const _helloText = (nickname: string, t: any): string => {
  return _i18nText(t, "comment.hello", {"nickname": nickname});
};
const _i18nText = (t:any, resourceKey: string, resourceValue: any ): string => {
  // NOTE: 第２引数のjaで内容文字列が変わるわけではないので任意
  return String(new IntlMessageFormat(t(resourceKey), "ja").format(resourceValue));
};
